import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { SiteHeader, SiteFooter } from "@/components/SiteChrome";
import { abs } from "@/lib/site";

// indexes.ts builds buckets from the full symbol index — dynamic-import it in the
// loader so the route module's static graph (bundled globally via routeTree) is
// light. The component reads everything (including the letter nav) from loaderData.
export const Route = createFileRoute("/index/a-z/$letter")({
  loader: async ({ params }) => {
    const { getLetterBucket, letterBuckets, letterIntro } = await import("@/data/indexes");
    const bucket = getLetterBucket(params.letter);
    if (!bucket) throw notFound();
    const nav = letterBuckets.map((b) => ({ slug: b.slug, label: b.label, count: b.symbols.length }));
    return { bucket, intro: letterIntro(bucket), nav };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [] };
    const { bucket } = loaderData;
    const title = `Symbols starting with ${bucket.label} — ${bucket.symbols.length} indexed entries`;
    const description = `Every indexed Unicode symbol whose official name begins with ${bucket.label}. ${bucket.symbols.length} entries, each linking to a full detail page with copy formats and codepoint metadata.`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: abs(`/index/a-z/${params.letter}`) },
        { property: "og:type", content: "article" },
      ],
      links: [{ rel: "canonical", href: abs(`/index/a-z/${params.letter}`) }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: title,
            url: `/index/a-z/${params.letter}`,
            numberOfItems: bucket.symbols.length,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: abs("/") },
              { "@type": "ListItem", position: 2, name: "A–Z index", item: abs("/index/a-z") },
              { "@type": "ListItem", position: 3, name: bucket.label, item: abs(`/index/a-z/${params.letter}`) },
            ],
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <div className="flex flex-1 items-center justify-center p-12 text-center">
        <div>
          <h1 className="font-display text-3xl font-bold">Letter not found</h1>
          <Link to="/index/a-z" className="mt-4 inline-block text-accent hover:underline">
            ← Back to A–Z index
          </Link>
        </div>
      </div>
      <SiteFooter />
    </div>
  ),
  component: LetterPage,
});

function LetterPage() {
  const { bucket, intro, nav } = Route.useLoaderData();
  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="flex-1">
        <section className="mx-auto w-full max-w-7xl px-4 pb-6 pt-8 sm:px-6 sm:pt-16">
          <nav className="mb-5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground sm:text-sm">
            <Link to="/" className="hover:text-foreground">Home</Link>
            <span>/</span>
            <Link to="/index/a-z" className="hover:text-foreground">A–Z</Link>
            <span>/</span>
            <span className="text-foreground">{bucket.label}</span>
          </nav>

          <h1 className="font-display text-3xl font-bold sm:text-5xl">
            Symbols starting with <span className="text-gradient">{bucket.label}</span>
          </h1>
          <div className="prose prose-invert mt-5 max-w-3xl space-y-4 text-foreground/90">
            {intro.map((p: string, i: number) => (
              <p key={i} className="text-[15px] leading-relaxed">
                {p}
              </p>
            ))}
          </div>
        </section>

        <section className="mx-auto w-full max-w-7xl px-4 pb-12 sm:px-6">
          <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {bucket.symbols.map((s: typeof bucket.symbols[number]) => (
              <li key={s.slug}>
                <Link
                  to="/symbol/$slug"
                  params={{ slug: s.slug }}
                  className="glass flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors hover:border-primary/40"
                >
                  <span className="flex h-9 w-9 flex-none items-center justify-center rounded-md border border-border bg-surface/60 text-xl">
                    {s.symbol}
                  </span>
                  <span className="min-w-0 flex-1 truncate">
                    <span className="font-medium">{s.name}</span>
                    <span className="block font-mono text-xs text-muted-foreground">
                      U+{s.primary.toString(16).toUpperCase().padStart(4, "0")}
                    </span>
                  </span>
                  <span className="text-muted-foreground">→</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>

        <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6">
          <h2 className="mb-4 font-display text-xl font-semibold">Jump to another letter</h2>
          <div className="flex flex-wrap gap-2">
            {nav
              .filter((b) => b.slug !== bucket.slug)
              .map((b) => (
                <Link
                  key={b.slug}
                  to="/index/a-z/$letter"
                  params={{ letter: b.slug }}
                  className="glass rounded-md px-3 py-1.5 text-sm transition-colors hover:border-primary/40"
                >
                  {b.label} <span className="text-xs text-muted-foreground">({b.count})</span>
                </Link>
              ))}
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
